import { z } from "zod";

import {
    HotelClinicNames,
    HotelClinicStatus,
    HotelClinicStatusActionType,
    IHotelClinicPassengerType,
} from "@/models/hotel-clinic.model";
import { HotelPickupReasons } from "@/types/hotel.types";

export const companionsSchema = z.object({
    name: z.string(),
    phone: z.string().optional(),
});

export const HotelClinicListBaseSchema = z.object({
    id: z.string(),
    uniqueIdentifier: z.string(),
    /** Assigned user (e.g. coordinator); distinct from driver id used elsewhere */
    userId: z.string().nullable().optional().default(null),
    hotel: z.string(),
    pickUp: z.string(),
    name: z.string(),
    companions: z.array(companionsSchema),
    reason: z.nativeEnum(HotelPickupReasons),
    notes: z.string(),
    cancelled: z.boolean(),
    _isPlaceholder: z.boolean().optional().default(false),
});

export const HotelClinicListClinicSchema = HotelClinicListBaseSchema.extend({
    type: z.literal(IHotelClinicPassengerType.CLINIC).default(IHotelClinicPassengerType.CLINIC),
    matchKey: z.string(),
    clinic: z.nativeEnum(HotelClinicNames).default(HotelClinicNames.ELIT_HAIR),
});

export const HotelClinicListCustomAuditInfoSchema = z.object({
    date: z.string().datetime({ offset: true }),
    userName: z.string(),
    userId: z.string(),
});

export enum HotelClinicRequestType {
    PASSENGER = "PASSENGER",
    COMPANION = "COMPANION",
    CUSTOM = "CUSTOM",
}

export const HotelClinicListCustomAsPassengerSchema = z.object({
    requestType: z.literal(HotelClinicRequestType.PASSENGER),
    operationId: z.string(),
});
export const HotelClinicListCustomAsCompanionSchema = z.object({
    requestType: z.literal(HotelClinicRequestType.COMPANION),
    ownerPassengerId: z.string(),
});
export const HotelClinicListCustomAsCustomSchema = z.object({
    requestType: z.literal(HotelClinicRequestType.CUSTOM),
});
export const HotelClinicListCustomTypeInfoSchema = z.discriminatedUnion("requestType", [
    HotelClinicListCustomAsPassengerSchema,
    HotelClinicListCustomAsCompanionSchema,
    HotelClinicListCustomAsCustomSchema,
]);
export const HotelClinicListCustomSchema = HotelClinicListBaseSchema.extend({
    type: z.literal(IHotelClinicPassengerType.CUSTOM),
    createInfo: HotelClinicListCustomAuditInfoSchema.optional(),
    updatedAt: z.string().datetime({ offset: true }),
    typeInfo: HotelClinicListCustomTypeInfoSchema,
    clinic: z.nativeEnum(HotelClinicNames),
});

export const HotelClinicListSchema = z.discriminatedUnion("type", [
    HotelClinicListClinicSchema,
    HotelClinicListCustomSchema,
]);

export const HotelClinicStatusMilestoneItemSchema = z
    .object({
        actionTime: z.string(),
        actionType: z.nativeEnum(HotelClinicStatusActionType),
        actorId: z.string(),
    })
    .nullable();

export const HotelClinicStatusMilestonesSchema = z.object({
    [HotelClinicStatus.PENDING]: HotelClinicStatusMilestoneItemSchema,
    [HotelClinicStatus.IN_TRANSIT]: HotelClinicStatusMilestoneItemSchema,
    [HotelClinicStatus.DONE]: HotelClinicStatusMilestoneItemSchema,
});

export const HotelClinicVehicleSchema = z.object({
    plate: z.string(),
    id: z.number(),
    model: z.string(),
});

const HotelClinicResolvedPassengerBaseSchema = z.object({
    listItemId: z.string(),
    createdAt: z.string(),
    status: z.nativeEnum(HotelClinicStatus).default(HotelClinicStatus.PENDING),
});

export const HotelClinicResolvedPassengerSchema = z.discriminatedUnion("type", [
    HotelClinicListClinicSchema.extend(HotelClinicResolvedPassengerBaseSchema.shape),
    HotelClinicListCustomSchema.extend(HotelClinicResolvedPassengerBaseSchema.shape),
]);

export const HotelClinicTaskResolvedSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable(),
    isDeleted: z.boolean(),
    hotel: z.string(),
    driverN2Id: z.string(),
    driverUserId: z.string(),
    vehicle: HotelClinicVehicleSchema.optional().nullable(),
    pickUp: z.string().datetime({ offset: true }),
    dropOff: z.string().datetime({ offset: true }),
    status: z.nativeEnum(HotelClinicStatus),
    statusMilestones: HotelClinicStatusMilestonesSchema,
    passengers: z.array(HotelClinicResolvedPassengerSchema),
});

export const HotelClinicManagerTasksByDateResponseSchema = z.object({
    tasks: z.array(HotelClinicTaskResolvedSchema),
    unassignedPassengers: z.array(HotelClinicListSchema),
});

export type HotelClinicManagerTasksByDateResponseSchemaType = z.infer<
    typeof HotelClinicManagerTasksByDateResponseSchema
>;
